import { Stack } from '@mui/material';
import { RowSelectionState, SortingState } from '@tanstack/react-table';
import { isEqual } from 'lodash-es';
import React, { useCallback, useEffect, useState } from 'react';

import { DataGrid } from '@sbu/components.datagrid';
import { DynamicFeatureFlag, useFeatureFlag } from '@sbu/hooks.featureflag';
import { UserSettingsTableSettings } from '@sbu/services.shared';
import { invalidateCacheData } from '@sbu/utils.apollo';
import { EventPropertyName, TrackEventName } from '@sbu/utils.tracking';

import {
  EntityListFragment,
  EntityListQueryOperationQuery,
  EntityListQueryOperationQueryVariables,
  EntitySortableField,
  SortDir,
  UploadEntityMutationsCsvMutation,
  UploadEntityMutationsCsvMutationVariables,
} from 'core/graphql';
import { FileUpload } from 'shared/components/form/FileUpload';
import {
  useDataGridRowSync,
  useFilterWithSort,
  useMutation,
  useQuery,
  useTrackEvent,
} from 'shared/hooks';

import { EntitiesSearchFilter } from '../EntitiesHeader/EntitiesSearchFilter';
import { EntityMetadataImportGuide } from '../EntitiesHeader/EntityMetadataImportGuide';
import { UPLOAD_ENTITY_METADATA_MUTATION } from '../mutations';
import {
  ENTITY_LIST_QUERY_OPERATION,
  useEntityCriticalities,
} from '../queries';
import { DEFAULT_SORT, DEFAULT_SORT_DIR, sortOptions } from '../sort';
import { ENTITY_QUERY_LIMIT } from '../utils';
import { useEntitiesContext } from './EntitiesContextProvider';
import { EntitiesDataGridAside } from './EntitiesDataGridAside';
import { getColumns, OBJECT_TYPE } from './utils';

export const EntitiesDataGrid = () => {
  const isColumnResizingEnabled = useFeatureFlag(
    DynamicFeatureFlag.EnableColumnResizing
  );

  const {
    isAsideOpen,
    selectedRow,
    selectedRows,
    onClickRow,
    onCloseAside,
    onSelectMultiple,
  } = useEntitiesContext();

  // pre-fetch entity criticalities
  useEntityCriticalities();

  const trackEvent = useTrackEvent();

  const {
    offset,
    q,
    sort,
    sorts,
    sortDir,
    onQueryChange,
    onSortingChange,
    onOffsetChange,
    mapSortingState,
  } = useFilterWithSort<EntitySortableField>({
    sortOptions,
    defaultSort: DEFAULT_SORT,
    defaultSortDir: DEFAULT_SORT_DIR,
  });

  const [tableSort, setTableSort] = useState<SortingState>([
    {
      id: sorts?.[0].prop || DEFAULT_SORT,
      desc: sortDir === SortDir.Desc,
    },
  ]);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { loading, data } = useQuery<
    EntityListQueryOperationQuery,
    EntityListQueryOperationQueryVariables
  >(ENTITY_LIST_QUERY_OPERATION, {
    variables: {
      q,
      limit: ENTITY_QUERY_LIMIT,
      offset,
      sort,
      sortDir,
    },
  });

  const [uploadEntities, { loading: uploading }] = useMutation<
    UploadEntityMutationsCsvMutation,
    UploadEntityMutationsCsvMutationVariables
  >(UPLOAD_ENTITY_METADATA_MUTATION, {
    update: (cache) => invalidateCacheData(cache, 'entities'),
  });

  useDataGridRowSync({
    currentData: data?.entities.objects,
    selectedRow,
    selectedRows,
    onClickRow,
    onSelectMultiple,
  });

  const referrerProps = { offset, q, sorts };

  const handleClose = useCallback(() => {
    setRowSelection({});
    onCloseAside();
  }, [onCloseAside]);

  useEffect(() => {
    if (!loading && !isEqual(mapSortingState(tableSort), sorts)) {
      onSortingChange(tableSort);
      handleClose();
    }
  }, [
    handleClose,
    loading,
    mapSortingState,
    onSortingChange,
    sorts,
    tableSort,
  ]);

  const handleRowClick = (row: EntityListFragment) => {
    trackEvent(TrackEventName.VIEW_ENTITY_SIDE_PANEL, {
      [EventPropertyName.ENTITY_ID]: row.id,
    });
    onClickRow(row);
  };

  const handleCheckboxClickAll = (
    rows: EntityListFragment[],
    isSelected: boolean
  ) => {
    if (isSelected) {
      onSelectMultiple([...rows]);
    } else {
      onSelectMultiple();
    }
  };

  const handleCheckboxClick = (
    row: EntityListFragment,
    isSelected: boolean
  ) => {
    let unorderedRows: EntityListFragment[] = [];
    if (selectedRows) {
      unorderedRows = [...selectedRows];
    } else if (selectedRow) {
      unorderedRows.push(selectedRow);
    }

    if (isSelected) {
      unorderedRows.push(row);
    } else {
      unorderedRows = unorderedRows.filter((r) => r.id !== row.id);
    }

    if (unorderedRows.length === 0) {
      onCloseAside();
    } else if (unorderedRows.length === 1) {
      if (unorderedRows[0].id === row.id) {
        handleRowClick(row);
      } else {
        handleRowClick(unorderedRows[0]);
      }
    } else if (unorderedRows.length > 1) {
      const orderedRows = (data?.entities?.objects || []).filter((obj) =>
        unorderedRows.some((r) => r.id === obj.id)
      );
      onSelectMultiple(orderedRows);
    }
  };

  const handleImport = async ({ addInfo, values }) => {
    const result = await uploadEntities({ variables: { file: values.file } });
    const { uploadEntityMutationsCsv: res } = result?.data ?? {};

    const itemCount = res?.uploadedEntitiesCount?.toLocaleString();
    const grammar = `${itemCount === '1' ? ' was' : 's were'}`;

    if (res && itemCount) {
      addInfo(
        `${itemCount} Entity configuration${grammar} imported in the background`
      );
    }
  };

  return (
    <>
      <DataGrid
        data-sumo-test='entities-data-grid'
        loading={loading}
        tableOptions={{
          data: data?.entities.objects || [],
          columns: getColumns(
            onQueryChange,
            handleCheckboxClickAll,
            handleCheckboxClick,
            data?.insightConfig?.threshold
          ),
          getRowId: (row) => row.id,
          manualSorting: true,
          sortDescFirst: true,
          state: {
            sorting: tableSort,
            rowSelection,
          },
          onSortingChange: setTableSort,
          enableRowSelection: true,
          enableMultiRowSelection: true,
          onRowSelectionChange: setRowSelection,
        }}
        openRowId={selectedRow?.id}
        renderHeader={() => (
          <Stack direction='row' justifyContent='space-between'>
            <EntitiesSearchFilter
              filterLabel=''
              onQueryChange={(query: string) => {
                onQueryChange(query);
                handleClose();
              }}
              queryString={q}
            />
            <FileUpload
              header='Import Metadata'
              intro={<EntityMetadataImportGuide />}
              onSubmit={handleImport}
              uploading={uploading}
            />
          </Stack>
        )}
        rowActions={[]}
        rowDataTestId={(row) => `entity-row-${row.original.id}`}
        onClickRow={(_e, row) => {
          if (row.id !== selectedRow?.id) {
            // select the row checkbox
            setRowSelection({ [row.id]: true });
          }
          handleRowClick(row.original);
        }}
        PaginationProps={{
          limit: ENTITY_QUERY_LIMIT,
          loading,
          offset,
          total: data?.entities.total,
          onOffsetChange: (offset: number) => {
            onOffsetChange(offset);
            handleClose();
          },
        }}
        EmptyStateProps={{
          objectType: OBJECT_TYPE,
          filtered: !!q,
          onClickReset: () => onQueryChange(''),
        }}
        enableResizing={isColumnResizingEnabled}
        enableReordering={isColumnResizingEnabled}
        tableName={'entitiesTableSettings' as keyof UserSettingsTableSettings}
      />
      {isAsideOpen && (
        <EntitiesDataGridAside
          insightConfigThreshold={data?.insightConfig?.threshold}
          referrerProps={referrerProps}
          onClose={handleClose}
        />
      )}
    </>
  );
};
